import { cn } from '@atlas/ui-primitives'
import type { MouseEvent, ReactNode } from 'react'

/**
 * Whether a click asked the browser for something other than plain navigation - a new
 * tab, a new window, a download, a saved link. Those belong to the browser, so they are
 * never intercepted.
 */
function isModifiedClick(event: MouseEvent): boolean {
	return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0
}

export interface LinkActionProps {
	/** The real destination. It is what makes this an anchor rather than a button. */
	href: string
	/**
	 * Called instead of navigating, on an unmodified primary click. The host performs its
	 * own in-app navigation; without it the browser follows `href` normally.
	 */
	onNavigate?: () => void
	/** Names the action when its content is a glyph, which alone says nothing. */
	'aria-label'?: string
	children: ReactNode
	className?: string
}

/**
 * A link a host can intercept: a real `<a href>` that hands an unmodified primary click
 * to `onNavigate` and lets the browser have every other kind.
 *
 * This is how a design-system component offers navigation without depending on a router.
 * The host wires `onNavigate` to whatever it navigates with, and the anchor keeps what a
 * button would have thrown away - cmd-click and middle-click to a new tab, the context
 * menu's "open in new tab" and "copy link", and a real URL in the status bar.
 */
export function LinkAction({ href, onNavigate, children, className, ...rest }: LinkActionProps) {
	return (
		<a
			href={href}
			aria-label={rest['aria-label']}
			onClick={(event) => {
				if (!onNavigate || isModifiedClick(event)) return
				event.preventDefault()
				onNavigate()
			}}
			className={cn(
				'rounded-ds-sm text-ds-accent outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ds-ring',
				className,
			)}
		>
			{children}
		</a>
	)
}
